import { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useSpace } from "@/hooks/useSpace";
import {
  disablePush,
  effectiveScopeOn,
  enablePush,
  getPushState,
  isDevicePushOn,
  resolvePushTypes,
  setPushScopeType,
  setPushType,
  watchPushPref,
  type PushEnvState,
  type PushPref,
} from "@/services/messaging";
import { watchUnitNotifyPolicy } from "@/services/my_account.repository";
import { PUSH_NOTIFY_TYPES, PUSH_TYPE_PERM, pushCopyDef, type PushNotifyType } from "@/domain/push_copy";
import { PUSH_TYPE_SCOPE, pushScopeKey, type NotifyPolicy } from "@/domain/session";
import { BIZ_META, SPACE_META, TENANT_BIZ, isBizReady, type BizKey, type TenantKey } from "@/domain/space";

/**
 * NotifyTab — D-2 알림 수신 설정(12 설계 §4-2 알림 · §8-D D-2 · §13 ③).
 *  ① 이 기기 앱 푸시 켜기/끄기 — 기기 단위(끄면 이 기기 토큰만 지움)
 *  ② 받을 알림 종류 — "공통"(그룹 종류: 가입 요청 등) + 내가 소속된 사업부마다 한 묶음(사업부 종류: 출하 등)
 *     표시 조건 = 내 권한(PUSH_TYPE_PERM). 사업부에서 꺼 둔 종류(notify_policy)는 흐리게 + "사업부에서 꺼 둠".
 *     토글은 바로 저장(저장 바 없음).
 */

/** 종류별 한 줄 설명 — 이름은 push_copy 정의(PUSH_COPY_DEFS)를 쓴다 */
const TYPE_DESC: Record<PushNotifyType, string> = {
  morning_plan: "평일 오전, 그날 출하 예정 물량 요약",
  ship_start: "오늘 첫 차량이 출하되면",
  ship_finish: "예정 물량을 모두 출하하면",
  ship_eod: "평일 오후, 그날 출하 결과 정리",
  signup: "새 사용자가 가입 승인을 기다릴 때 · 관리자(법인 관리자는 자기 법인 신청만)",
  sales_sync: "영업 자료 자동 동기화가 실패하면",
  rqc_drift: "출하량 불일치를 감지하면",
};

interface Group {
  key: string;
  label: string;
  /** null = 공통(그룹 종류) */
  scope: { tenant: TenantKey; biz: BizKey; key: string } | null;
  types: PushNotifyType[];
}

function NtfSwitch({ checked, disabled, onChange, label }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="ntf-switch" aria-label={label}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="ntf-switch-track">
        <span className="ntf-switch-thumb" />
      </span>
    </label>
  );
}

export function NotifyTab() {
  const { user, deviceId, device, sharedSession } = useAuth();
  const { tenants, myBizs, hasPermIn, membership } = useSpace();
  // 가입 알림(signup) — 전체 관리자 + 법인 관리자(자기 법인 신청을 받음, 함수 notifySignup 2026-10-27)
  const isTenantAdmin = membership.adminOf.length > 0;
  const toast = useToast();
  const uid = user?.uid ?? null;
  const [env, setEnv] = useState<PushEnvState | null>(null);
  const [pref, setPref] = useState<PushPref>({ enabled: false, tokens: {}, types: resolvePushTypes(undefined), scopes: {} });
  const [prefLoaded, setPrefLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Record<string, NotifyPolicy | null>>({});

  useEffect(() => {
    let alive = true;
    void getPushState().then((s) => alive && setEnv(s));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!uid) return;
    return watchPushPref(
      uid,
      (p) => {
        setPref(p);
        setPrefLoaded(true);
      },
      () => setPrefLoaded(true),
    );
  }, [uid]);

  // 내 소속 사업부마다 한 묶음 — 소속 문서 bizs(다중, 준비중 포함). 소속 사업부가 없으면(전체 admin·옛 계정) 그 법인의 운영 중 사업부
  const units = useMemo(
    () =>
      tenants.flatMap((t) =>
        (myBizs[t]?.length ? myBizs[t]! : TENANT_BIZ[t].filter(isBizReady)).map((b) => ({ tenant: t, biz: b, key: pushScopeKey(t, b) })),
      ),
    [tenants, myBizs],
  );
  const unitsKey = units.map((u) => u.key).join(",");

  useEffect(() => {
    const unsubs = units.map((u) => watchUnitNotifyPolicy(u.tenant, u.biz, (p) => setPolicies((prev) => ({ ...prev, [u.key]: p }))));
    return () => unsubs.forEach((f) => f());
    // unitsKey 가 같으면 같은 구독
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsKey]);

  const groups: Group[] = useMemo(() => {
    const eligible = (t: PushNotifyType, tenant?: TenantKey) => {
      const perm = PUSH_TYPE_PERM[t];
      if (!perm) return !!user?.isAdmin || (t === "signup" && isTenantAdmin); // 관리자 전용 종류(가입은 법인 관리자도)
      return hasPermIn(perm, tenant);
    };
    const out: Group[] = [];
    const common = PUSH_NOTIFY_TYPES.filter((t) => PUSH_TYPE_SCOPE[t] === "group" && eligible(t));
    if (common.length) out.push({ key: "common", label: "공통", scope: null, types: common });
    for (const u of units) {
      const types = PUSH_NOTIFY_TYPES.filter((t) => PUSH_TYPE_SCOPE[t] === "unit" && eligible(t, u.tenant));
      if (types.length) {
        out.push({ key: u.key, label: `${SPACE_META[u.tenant].short} · ${BIZ_META[u.biz].label}`, scope: u, types });
      }
    }
    return out;
  }, [units, hasPermIn, user?.isAdmin, isTenantAdmin]);

  if (!user) return null;

  const deviceOn = isDevicePushOn(pref, deviceId, device?.push_token ?? null) && env?.permission === "granted";
  const blockedReason = sharedSession
    ? "공용 PC로 로그인해 이 기기에서는 앱 푸시를 켤 수 없습니다."
    : env == null
      ? ""
      : env.needsStandalone
        ? "아이폰은 Safari 공유 → ‘홈 화면에 추가’ 한 앱에서만 받을 수 있습니다."
        : !env.supported
          ? "이 브라우저는 앱 푸시를 지원하지 않습니다."
          : env.permission === "denied"
            ? "브라우저에서 알림이 차단돼 있습니다. 브라우저(기기) 설정에서 이 사이트 알림을 허용한 뒤 다시 켜세요."
            : "";

  async function toggleDevice(next: boolean) {
    if (!uid || busy) return;
    setBusy(true);
    try {
      if (next) {
        const res = await enablePush(uid);
        if (res.ok) toast.success("이 기기에서 앱 푸시를 받습니다.");
        else toast.error(res.reason ?? "앱 푸시를 켜지 못했습니다.");
      } else {
        await disablePush(uid);
        toast.info("이 기기의 앱 푸시를 껐습니다. 다른 기기는 그대로입니다.");
      }
    } catch (e) {
      toast.error(`처리하지 못했습니다: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      void getPushState().then(setEnv);
    }
  }

  async function toggleType(g: Group, t: PushNotifyType, next: boolean) {
    if (!uid) return;
    const rk = `${g.key}:${t}`;
    setRowBusy(rk);
    // 낙관 갱신 — 구독이 곧 덮는다
    setPref((p) =>
      g.scope
        ? { ...p, scopes: { ...p.scopes, [g.scope.key]: { ...p.scopes[g.scope.key], [t]: next } } }
        : { ...p, types: { ...p.types, [t]: next } },
    );
    try {
      if (g.scope) await setPushScopeType(uid, g.scope.key, t, next);
      else await setPushType(uid, t, next);
    } catch (e) {
      toast.error(`저장하지 못했습니다: ${(e as Error).message}`);
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <div className="ma-pane">
      <section className="ma-sec">
        <h3 className="ma-sec-title">이 기기 앱 푸시</h3>
        <div className="ma-card ma-ntf-device">
          <div className="ma-ntf-text">
            <div className="ma-ntf-label">
              이 기기({device?.name || (sharedSession ? "공용 PC" : "현재 브라우저")})에서 앱 푸시 받기
            </div>
            <div className="ma-help">기기마다 따로 켭니다 · 아이폰은 홈 화면에 추가한 앱에서만 받을 수 있습니다</div>
            {blockedReason && (
              <div className="ma-help is-warn">
                <Icon name="warning" size={14} /> {blockedReason}
              </div>
            )}
          </div>
          <NtfSwitch
            label="이 기기 앱 푸시"
            checked={deviceOn}
            disabled={busy || env == null || (!!blockedReason && !deviceOn)}
            onChange={(v) => void toggleDevice(v)}
          />
        </div>
      </section>

      <section className="ma-sec">
        <div className="ma-sec-hd">
          <h3 className="ma-sec-title">받을 알림 종류</h3>
          <span className="ma-help">내 권한으로 볼 수 있는 업무 알림만 보입니다</span>
        </div>
        {!prefLoaded ? (
          <div className="ma-skel-list" aria-busy="true">
            <span className="ma-skel" />
            <span className="ma-skel" />
          </div>
        ) : groups.length === 0 ? (
          <div className="empty-state ma-empty">
            <Icon name="bell" size={24} />
            <div className="empty-msg">받을 수 있는 업무 알림이 없습니다</div>
            <div className="ma-help">권한이 생기면 여기에 나타납니다.</div>
          </div>
        ) : (
          groups.map((g) => {
            const policy = g.scope ? policies[g.scope.key] : null;
            return (
              <div className="ma-ntf-group" key={g.key}>
                <div className="ma-ntf-group-hd">
                  <span className={`ma-ntf-group-label${g.scope ? ` is-${g.scope.tenant}` : ""}`}>{g.label}</span>
                  {g.scope && <span className="ma-help">이 사업부 업무 알림</span>}
                </div>
                {g.types.map((t) => {
                  const orgOff = policy?.kinds[t] === false;
                  const on = g.scope ? effectiveScopeOn(pref, g.scope.key, t) : pref.types[t];
                  return (
                    <div className={`ma-card ma-ntf-row${orgOff ? " is-org-off" : ""}`} key={t}>
                      <div className="ma-ntf-text">
                        <div className="ma-ntf-label">
                          {pushCopyDef(t).name}
                          {orgOff && <span className="badge">사업부에서 꺼 둠</span>}
                        </div>
                        <div className="ma-help">{TYPE_DESC[t]}</div>
                      </div>
                      <NtfSwitch
                        label={`${g.label} ${pushCopyDef(t).name}`}
                        checked={!orgOff && on}
                        disabled={orgOff || rowBusy === `${g.key}:${t}`}
                        onChange={(v) => void toggleType(g, t, v)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
        <p className="ma-help ma-foot">푸시를 끈 기기에서도 화면 위 벨 알림은 그대로 쌓입니다 · 토글은 바로 저장됩니다</p>
      </section>
    </div>
  );
}
